import React, { useEffect, useMemo, useCallback, useState } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import {
  Box,
  Paper,
  Typography,
  Stack,
  Grid,
  Divider,
  Tabs,
  Tab,
  TextField,
  Button,
  Chip,
  Alert,
  Snackbar,
  CircularProgress,
  ToggleButtonGroup,
  ToggleButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Checkbox,
  Avatar,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Pagination,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import ScrollableTableContainer from "../../common/ScrollableTableContainer";

import SendIcon from "@mui/icons-material/Send";
import PeopleIcon from "@mui/icons-material/People";
import MessageIcon from "@mui/icons-material/Message";
import NotificationsIcon from "@mui/icons-material/Notifications";
import SmsIcon from "@mui/icons-material/Sms";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import ClearAllIcon from "@mui/icons-material/ClearAll";
import WifiIcon from "@mui/icons-material/Wifi";
import WifiOffIcon from "@mui/icons-material/WifiOff";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";

import { useSocket } from "../../../hooks/useSocket";

const API_BASE =
  process.env.REACT_APP_BACKEND_URL ||
  process.env.REACT_APP_API_URL ||
  "https://app.krush.in/api";

const BROADCAST_ROLES = [
  { value: "all", label: "All Users (Except Admins)", canHaveSubordinates: false },
  { value: "manager", label: "Managers", canHaveSubordinates: true },
  { value: "hr", label: "HR Personnel", canHaveSubordinates: false },
  { value: "sub_admin", label: "Admins", canHaveSubordinates: true },
  { value: "dealer", label: "Dealers", canHaveSubordinates: true },
  { value: "group_admin", label: "Dealers", canHaveSubordinates: true },
  { value: "user", label: "Standard Users", canHaveSubordinates: false },
  { value: "client", label: "Clients", canHaveSubordinates: false },
  { value: "field_emp", label: "Field Employees", canHaveSubordinates: false },
  { value: "office_emp", label: "Office Employees", canHaveSubordinates: false },
];

const DELIVERY = [
  { value: "Notification", label: "In-App Notification", icon: <NotificationsIcon fontSize="small" /> },
  { value: "SMS", label: "SMS", icon: <SmsIcon fontSize="small" /> },
];

const safeArr = (data) => {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (Array.isArray(data.data)) return data.data;
  if (Array.isArray(data.users)) return data.users;
  return [];
};

const fmtDateTime = (dateString) => {
  const d = new Date(dateString);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// ✅ Token helper (Bearer)
const getAuthToken = () => localStorage.getItem("token") || localStorage.getItem("authToken");

function RecipientTabPanel({ value, index, children }) {
  return (
    <Box role="tabpanel" hidden={value !== index} sx={{ pt: 2 }}>
      {value === index && children}
    </Box>
  );
}

export default function MessageManagement({ embedded = false }) {
  const { socket, connected } = useSocket();
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down("sm"));
  const currentUser = useSelector((state) => state.auth?.user);
  const currentRole = String(currentUser?.role || "").trim().toLowerCase();
  const isSubAdmin = currentRole === "sub_admin";
  const isDealer = currentRole === "group_admin" || currentRole === "dealer";
  const isScopedSender = isSubAdmin || isDealer;

  // Tabs: 0 Role, 1 Individuals, 2 Subordinates
  const [tab, setTab] = useState(() => {
    if (isDealer) return 1;
    if (isSubAdmin) return 1;
    return 0;
  });
  const [deliveryType, setDeliveryType] = useState("Notification");

  // Role broadcast
  const [selectedRole, setSelectedRole] = useState(() => {
    if (isSubAdmin) return "group_admin";
    return "all";
  });

  // Individuals (MULTI)
  const [allUsers, setAllUsers] = useState([]);
  const [individualSearch, setIndividualSearch] = useState("");
  const [selectedIndividuals, setSelectedIndividuals] = useState([]);

  // Subordinates flow
  const [roleUsers, setRoleUsers] = useState([]);
  const [roleUserSearch, setRoleUserSearch] = useState("");
  const [selectedRoleUser, setSelectedRoleUser] = useState(null);

  const [subordinateUsers, setSubordinateUsers] = useState([]);
  const [subSearch, setSubSearch] = useState("");
  const [selectedSubordinates, setSelectedSubordinates] = useState([]);

  // Message + history
  const [message, setMessage] = useState("");
  const [messageHistory, setMessageHistory] = useState([]);
  const [historySearch, setHistorySearch] = useState("");
  const [historyPage, setHistoryPage] = useState(1);
  const [historyRowsPerPage, setHistoryRowsPerPage] = useState(10);

  // Loading
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingRoleUsers, setLoadingRoleUsers] = useState(false);
  const [loadingSubs, setLoadingSubs] = useState(false);
  const [sending, setSending] = useState(false);

  // Snackbar
  const [snack, setSnack] = useState({ open: false, severity: "success", msg: "" });
  const notify = useCallback((severity, msg) => setSnack({ open: true, severity, msg }), []);

  // Socket optional
  useEffect(() => {
    if (!socket) return;
    return () => {};
  }, [socket]);

  // ✅ Fetch users list from: GET /api/admin/users
  const fetchUsers = useCallback(async () => {
    setLoadingUsers(true);
    try {
      const token = getAuthToken();
      if (!token) {
        notify("warning", "Token missing. Please login again.");
        return;
      }

      const { data } = await axios.get(`${API_BASE}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setAllUsers(safeArr(data));
    } catch (e) {
      console.error(e);
      notify("error", e?.response?.data?.msg || "Failed to fetch users.");
    } finally {
      setLoadingUsers(false);
    }
  }, [notify]);

  // ✅ Fetch history from: GET /api/messaging/history
  const fetchHistory = useCallback(async () => {
    try {
      const token = getAuthToken();
      if (!token) return;

      const { data } = await axios.get(`${API_BASE}/messaging/history`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setMessageHistory(safeArr(data));
    } catch (e) {
      console.error(e);
      // history not critical
    }
  }, []);

  useEffect(() => {
    fetchUsers();
    fetchHistory();
  }, [fetchUsers, fetchHistory]);

  // Subordinates mode config
  const selectedRoleConfig = useMemo(
    () => BROADCAST_ROLES.find((r) => r.value === selectedRole),
    [selectedRole]
  );

  const availableTabs = useMemo(() => {
    if (isDealer) return [1];
    if (isSubAdmin) return [1, 2];
    return [0, 1, 2];
  }, [isDealer, isSubAdmin]);

  const availableRoleOptions = useMemo(() => {
    if (isDealer) return [];
    if (isSubAdmin) return BROADCAST_ROLES.filter((role) => role.value === "group_admin");
    return BROADCAST_ROLES;
  }, [isDealer, isSubAdmin]);

  useEffect(() => {
    if (!availableTabs.includes(tab)) {
      setTab(availableTabs[0] || 1);
    }
  }, [availableTabs, tab]);

  useEffect(() => {
    if (!availableRoleOptions.length) return;
    if (!availableRoleOptions.some((role) => role.value === selectedRole)) {
      setSelectedRole(availableRoleOptions[0].value);
    }
  }, [availableRoleOptions, selectedRole]);

  // ✅ Fetch role users list (parent users) for hierarchy selection
  // NOTE: aapke code me endpoint: /users/by-role/:role
  // ensure backend has it with /api prefix: /api/users/by-role/:role
  useEffect(() => {
    if (tab !== 2) return;

    if (!selectedRoleConfig?.canHaveSubordinates) {
      setRoleUsers([]);
      setSelectedRoleUser(null);
      setSubordinateUsers([]);
      setSelectedSubordinates([]);
      return;
    }

    const run = async () => {
      setLoadingRoleUsers(true);
      try {
        const token = getAuthToken();
        if (!token) return;

        const { data } = await axios.get(`${API_BASE}/users/by-role/${selectedRole}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        setRoleUsers(safeArr(data));
      } catch (e) {
        console.error(e);
        notify("error", `Failed to fetch ${selectedRoleConfig.label} list.`);
        setRoleUsers([]);
      } finally {
        setLoadingRoleUsers(false);
      }
    };

    run();
  }, [tab, selectedRole, selectedRoleConfig, notify]);

  // ✅ Fetch subordinates: GET /api/messaging/subordinates/:userId
  useEffect(() => {
    if (tab !== 2) return;

    if (!selectedRoleUser?._id) {
      setSubordinateUsers([]);
      setSelectedSubordinates([]);
      return;
    }

    const run = async () => {
      setLoadingSubs(true);
      try {
        const token = getAuthToken();
        if (!token) return;

        const { data } = await axios.get(`${API_BASE}/messaging/subordinates/${selectedRoleUser._id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        setSubordinateUsers(safeArr(data));
      } catch (e) {
        console.error(e);
        notify("error", "Failed to fetch subordinate users.");
        setSubordinateUsers([]);
      } finally {
        setLoadingSubs(false);
      }
    };

    run();
  }, [tab, selectedRoleUser, notify]);

  // Filters
  const individualOptions = useMemo(() => {
    const q = individualSearch.trim().toLowerCase();
    if (!q) return allUsers.slice(0, 80);
    return allUsers
      .filter((u) => (u.username || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q))
      .slice(0, 80);
  }, [allUsers, individualSearch]);

  const roleUserOptions = useMemo(() => {
    const q = roleUserSearch.trim().toLowerCase();
    if (!q) return roleUsers;
    return roleUsers.filter(
      (u) => (u.username || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q)
    );
  }, [roleUsers, roleUserSearch]);

  const filteredSubs = useMemo(() => {
    const q = subSearch.trim().toLowerCase();
    if (!q) return subordinateUsers;
    return subordinateUsers.filter(
      (u) => (u.username || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q)
    );
  }, [subordinateUsers, subSearch]);

  const selectAllSubordinates = () => setSelectedSubordinates([...filteredSubs]);
  const clearAllSubordinates = () => setSelectedSubordinates([]);

  // ✅ Build payload as per backend
  // Backend supports:
  // role => {type:'role', value:'all'|'Manager'...}
  // individual => {type:'individual', value:userId}
  // subordinates => {type:'subordinates', parentUserId, userIds:[]}
  const recipientPayload = useMemo(() => {
    if (tab === 0) {
      return {
        ok: !!selectedRole,
        recipient: { type: "role", value: selectedRole },
        label: availableRoleOptions.find((r) => r.value === selectedRole)?.label || selectedRole,
      };
    }

    if (tab === 1) {
      const ids = selectedIndividuals.map((u) => u._id).filter(Boolean);
      if (ids.length === 1) {
        return { ok: true, recipient: { type: "individual", value: ids[0] }, label: selectedIndividuals[0]?.username };
      }
      if (ids.length > 1) {
        // ✅ MULTI to backend using subordinates
        // NOTE: parentUserId optional fix recommended in backend (below)
        return {
          ok: true,
          recipient: { type: "subordinates", parentUserId: null, userIds: ids },
          label: `${ids.length} selected users`,
        };
      }
      return { ok: false, recipient: null, label: "" };
    }

    if (tab === 2) {
      const ids = selectedSubordinates.map((u) => u._id).filter(Boolean);
      return {
        ok: !!selectedRoleUser?._id && ids.length > 0,
        recipient: { type: "subordinates", parentUserId: selectedRoleUser?._id, userIds: ids },
        label: `${ids.length} users under ${selectedRoleUser?.username || ""}`,
      };
    }

    return { ok: false };
  }, [tab, selectedRole, selectedIndividuals, selectedSubordinates, selectedRoleUser, availableRoleOptions]);

  const isFormValid = Boolean(message.trim()) && recipientPayload.ok;

  const filteredHistory = useMemo(() => {
    const query = String(historySearch || "").trim().toLowerCase();
    if (!query) return messageHistory;
    return messageHistory.filter((entry) =>
      [entry?.type, entry?.recipient, entry?.message, entry?.status]
        .join(" ")
        .toLowerCase()
        .includes(query)
    );
  }, [historySearch, messageHistory]);

  const historyPageCount = Math.max(1, Math.ceil(filteredHistory.length / historyRowsPerPage));
  const pagedHistory = useMemo(() => {
    const start = (historyPage - 1) * historyRowsPerPage;
    return filteredHistory.slice(start, start + historyRowsPerPage);
  }, [filteredHistory, historyPage, historyRowsPerPage]);

  useEffect(() => {
    if (historyPage > historyPageCount) setHistoryPage(1);
  }, [historyPage, historyPageCount]);

  // ✅ Send message: POST /api/messaging/send
  const handleSend = async () => {
    if (!message.trim()) return notify("warning", "Message cannot be empty.");
    if (!recipientPayload.ok) return notify("warning", "Please select recipients.");

    setSending(true);
    try {
      const token = getAuthToken();
      if (!token) {
        notify("warning", "Token missing. Please login again.");
        return;
      }

      const { data } = await axios.post(
        `${API_BASE}/messaging/send`,
        {
          recipient: recipientPayload.recipient,
          deliveryType,
          message: message.trim(),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const entry = {
        _id: data?._id || Date.now(),
        type: deliveryType,
        recipient: data?.recipient || recipientPayload.label,
        status: data?.status || "Sent",
        message: message.trim(),
        createdAt: new Date().toISOString(),
      };
      setMessageHistory((prev) => [entry, ...prev]);

      notify("success", data?.msg || "Message sent successfully.");
      setMessage("");

      if (tab === 1) setSelectedIndividuals([]);
      if (tab === 2) setSelectedSubordinates([]);
    } catch (e) {
      console.error(e);
      notify("error", e?.response?.data?.msg || "Failed to send message.");
    } finally {
      setSending(false);
    }
  };

  const TopStat = ({ label, value, icon, description, tone = "primary" }) => (
    <Paper
      variant="outlined"
      sx={{
        p: 2.2,
        borderRadius: 3.5,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
        flexWrap: "wrap",
        borderColor: alpha(theme.palette.divider, 0.9),
        boxShadow: "0 18px 40px rgba(15,23,42,0.05)",
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Avatar
          sx={{
            width: 48,
            height: 48,
            bgcolor: alpha(theme.palette[tone]?.main || theme.palette.primary.main, 0.12),
            color: theme.palette[tone]?.main || theme.palette.primary.main,
          }}
        >
          {icon}
        </Avatar>
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 700 }}>
            {label}
          </Typography>
          <Typography variant="h5" sx={{ lineHeight: 1.1, fontWeight: 900, color: "#0f172a", mt: 0.3 }}>
            {value}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.45 }}>
            {description}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  );

  return (
    <Box sx={{ p: embedded ? 0 : { xs: 2, md: 3 } }}>
      {!embedded ? (
        <Paper
          elevation={0}
          variant="outlined"
          sx={{
            p: { xs: 2, md: 2.5 },
            borderRadius: 4,
            mb: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            flexWrap: "wrap",
            borderColor: alpha(theme.palette.divider, 0.9),
            boxShadow: "0 18px 40px rgba(15,23,42,0.05)",
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar sx={{ width: 48, height: 48, bgcolor: alpha(theme.palette.primary.main, 0.12), color: theme.palette.primary.main }}>
              <MessageIcon />
            </Avatar>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 800 }}>
                Messages
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Send notifications and SMS to your users instantly
              </Typography>
            </Box>
          </Stack>

          <Tooltip title={connected ? "Socket connected" : "Socket disconnected"}>
            <Chip
              icon={connected ? <WifiIcon /> : <WifiOffIcon />}
              label={connected ? "Live Connected" : "Disconnected"}
              color={connected ? "success" : "default"}
              variant={connected ? "filled" : "outlined"}
            />
          </Tooltip>
        </Paper>
      ) : null}

      <Stack
        direction={{ xs: "column", lg: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", lg: "center" }}
        spacing={1.5}
        sx={{ mb: 2 }}
      >
        <Grid container spacing={2} sx={{ flex: 1, m: 0 }}>
          <Grid item xs={12} md={4}>
            <TopStat
              label="Users Loaded"
              value={loadingUsers ? "Loading..." : allUsers.length}
              icon={<PeopleIcon fontSize="small" />}
              description="Available users for targeting"
              tone="primary"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TopStat
              label="Recent History"
              value={messageHistory.length}
              icon={<HistoryOutlinedIcon fontSize="small" />}
              description="Messages already sent"
              tone="secondary"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TopStat
              label="Delivery"
              value={deliveryType}
              icon={deliveryType === "SMS" ? <SmsIcon fontSize="small" /> : <NotificationsIcon fontSize="small" />}
              description={deliveryType === "SMS" ? "SMS sending channel active" : "In-app notifications active"}
              tone={deliveryType === "SMS" ? "info" : "success"}
            />
          </Grid>
        </Grid>

        <Paper
          variant="outlined"
          sx={{
            p: 1,
            borderRadius: 3,
            borderColor: alpha(theme.palette.divider, 0.9),
            boxShadow: "0 18px 40px rgba(15,23,42,0.05)",
          }}
        >
          <ToggleButtonGroup
            exclusive
            value={deliveryType}
            onChange={(_, v) => v && setDeliveryType(v)}
            size="small"
            sx={{
              "& .MuiToggleButton-root": {
                px: 2.2,
                py: 1,
                border: 0,
                borderRadius: "12px !important",
                textTransform: "none",
                fontWeight: 800,
              },
            }}
          >
            {DELIVERY.map((d) => (
              <ToggleButton key={d.value} value={d.value}>
                <Stack direction="row" spacing={1} alignItems="center">
                  {d.icon}
                  <span>{d.label}</span>
                </Stack>
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Paper>
      </Stack>

      <Grid container spacing={2}>
        <Grid item xs={12} lg={7}>
          <Paper
            variant="outlined"
            sx={{
              borderRadius: 4,
              overflow: "hidden",
              borderColor: alpha(theme.palette.divider, 0.9),
              boxShadow: "0 18px 40px rgba(15,23,42,0.05)",
            }}
          >
            <Box sx={{ p: 2.2, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <CampaignOutlinedIcon sx={{ color: theme.palette.primary.main }} />
                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                  Compose & Send
                </Typography>
              </Stack>
              <Tooltip title={connected ? "Socket connected" : "Socket disconnected"}>
                <Chip
                  icon={connected ? <WifiIcon /> : <WifiOffIcon />}
                  label={connected ? "Live Connected" : "Disconnected"}
                  color={connected ? "success" : "default"}
                  variant={connected ? "filled" : "outlined"}
                />
              </Tooltip>
            </Box>

            <Divider />

            <Box sx={{ p: 2.2 }}>
              <Stack spacing={2.2}>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, borderColor: alpha(theme.palette.primary.main, 0.14), bgcolor: alpha(theme.palette.primary.main, 0.02) }}>
                  <Stack direction="row" spacing={1.2} alignItems="center" sx={{ mb: 1.4 }}>
                    <Avatar sx={{ width: 34, height: 34, bgcolor: alpha(theme.palette.primary.main, 0.12), color: theme.palette.primary.main }}>1</Avatar>
                    <Box>
                      <Typography sx={{ fontWeight: 900, color: "#0f172a" }}>Step 1: Select Audience</Typography>
                      <Typography variant="body2" color="text.secondary">Choose a role, individual users, or hierarchy-based subordinates.</Typography>
                    </Box>
                  </Stack>

                  <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto">
                    {availableTabs.includes(0) && <Tab value={0} label="By Role" />}
                    {availableTabs.includes(1) && <Tab value={1} label="Individuals" />}
                    {availableTabs.includes(2) && <Tab value={2} label="Subordinates" />}
                  </Tabs>

              {/* Role */}
              <RecipientTabPanel value={tab} index={0}>
                <Stack spacing={2}>
                  <Alert severity="info" variant="outlined">
                    Select a role and send to all active users in that role.
                  </Alert>

                  <FormControl fullWidth>
                    <InputLabel id="role-label">Target Role</InputLabel>
                    <Select labelId="role-label" label="Target Role" value={selectedRole} onChange={(e) => setSelectedRole(e.target.value)}>
                      {availableRoleOptions.map((r) => (
                        <MenuItem key={r.value} value={r.value}>
                          <Stack direction="row" spacing={1} alignItems="center" sx={{ width: "100%" }}>
                            <Typography sx={{ fontWeight: 700 }}>{r.label}</Typography>
                            {r.canHaveSubordinates && <Chip size="small" label="Has hierarchy" variant="outlined" />}
                          </Stack>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2.5, display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
                        Selected Audience
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {recipientPayload.label || "-"}
                      </Typography>
                    </Box>
                    <Chip label="Broadcast" color="primary" variant="outlined" />
                  </Paper>
                </Stack>
              </RecipientTabPanel>

              {/* Individuals */}
              <RecipientTabPanel value={tab} index={1}>
                <Stack spacing={2}>
                  <Alert severity="info" variant="outlined">
                    {isScopedSender
                      ? "Only users inside your own hierarchy are available here."
                      : "Search users and select multiple recipients. (Backend: will send as multi-users payload)"}
                  </Alert>

                  <Autocomplete
                    multiple
                    disableCloseOnSelect
                    options={individualOptions}
                    value={selectedIndividuals}
                    loading={loadingUsers}
                    onChange={(_, value) => setSelectedIndividuals(value)}
                    inputValue={individualSearch}
                    onInputChange={(_, v) => setIndividualSearch(v)}
                    getOptionLabel={(opt) => `${opt.username || "User"}${opt.email ? ` • ${opt.email}` : ""}`}
                    isOptionEqualToValue={(a, b) => a._id === b._id}
                    renderOption={(props, option, { selected }) => (
                      <li {...props} key={option._id}>
                        <Checkbox checked={selected} sx={{ mr: 1 }} />
                        <Stack sx={{ flex: 1 }}>
                          <Typography sx={{ fontWeight: 700 }}>{option.username || "User"}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {option.email || "-"} • {option.role || "-"}
                          </Typography>
                        </Stack>
                        <Chip size="small" label={option.role || "role"} variant="outlined" />
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Search users (name/email)"
                        placeholder="Type to search..."
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <>
                              <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />
                              {params.InputProps.startAdornment}
                            </>
                          ),
                          endAdornment: (
                            <>
                              {loadingUsers ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />

                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2.5, display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
                        Selected Users
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {selectedIndividuals.length} selected
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      {selectedIndividuals.slice(0, 5).map((u) => (
                        <Chip key={u._id} label={u.username} size="small" />
                      ))}
                      {selectedIndividuals.length > 5 && (
                        <Chip size="small" label={`+${selectedIndividuals.length - 5} more`} variant="outlined" />
                      )}
                    </Stack>
                  </Paper>
                </Stack>
              </RecipientTabPanel>

              {/* Subordinates */}
              <RecipientTabPanel value={tab} index={2}>
                <Stack spacing={2}>
                  <Alert severity="info" variant="outlined">
                    Select parent role → select parent user → multi-select subordinates.
                  </Alert>

                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth>
                        <InputLabel id="sub-role">Step 1: Role Type</InputLabel>
                        <Select
                          labelId="sub-role"
                          label="Step 1: Role Type"
                          value={selectedRole}
                          onChange={(e) => {
                            setSelectedRole(e.target.value);
                            setSelectedRoleUser(null);
                            setRoleUserSearch("");
                            setSubordinateUsers([]);
                            setSelectedSubordinates([]);
                          }}
                        >
                          {availableRoleOptions.filter((r) => r.canHaveSubordinates).map((r) => (
                            <MenuItem key={r.value} value={r.value}>
                              {r.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Autocomplete
                        options={roleUserOptions}
                        value={selectedRoleUser}
                        loading={loadingRoleUsers}
                        inputValue={roleUserSearch}
                        onInputChange={(_, v) => setRoleUserSearch(v)}
                        onChange={(_, v) => {
                          setSelectedRoleUser(v);
                          setSelectedSubordinates([]);
                          setSubSearch("");
                        }}
                        getOptionLabel={(opt) => `${opt.username || "User"}${opt.email ? ` • ${opt.email}` : ""}`}
                        isOptionEqualToValue={(a, b) => a._id === b._id}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={`Step 2: Select Parent (${selectedRoleConfig?.label?.replace(/s$/, "") || "User"})`}
                            placeholder={loadingRoleUsers ? "Loading..." : "Search by name/email..."}
                            InputProps={{
                              ...params.InputProps,
                              startAdornment: (
                                <>
                                  <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />
                                  {params.InputProps.startAdornment}
                                </>
                              ),
                              endAdornment: (
                                <>
                                  {loadingRoleUsers ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                          />
                        )}
                      />
                    </Grid>
                  </Grid>

                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2.5 }}>
                    <Stack spacing={1.5}>
                      <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent="space-between" flexWrap="wrap">
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
                            Step 3: Select Subordinates
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {selectedRoleUser ? `Under ${selectedRoleUser.username} • ${subordinateUsers.length} available` : "Select a parent user first."}
                          </Typography>
                        </Box>

                        <Stack direction="row" spacing={1} flexWrap="wrap">
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<DoneAllIcon />}
                            onClick={selectAllSubordinates}
                            disabled={!selectedRoleUser || loadingSubs || filteredSubs.length === 0}
                          >
                            Select All
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<ClearAllIcon />}
                            onClick={clearAllSubordinates}
                            disabled={selectedSubordinates.length === 0}
                          >
                            Clear
                          </Button>
                        </Stack>
                      </Stack>

                      <TextField
                        value={subSearch}
                        onChange={(e) => setSubSearch(e.target.value)}
                        label="Search subordinates"
                        placeholder="Search by name/email..."
                        InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: "text.secondary" }} /> }}
                        disabled={!selectedRoleUser || loadingSubs}
                        fullWidth
                      />

                      {loadingSubs ? (
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 2 }}>
                          <CircularProgress size={20} />
                          <Typography variant="body2" color="text.secondary">
                            Loading subordinates...
                          </Typography>
                        </Stack>
                      ) : !selectedRoleUser ? (
                        <Alert severity="warning" variant="outlined">
                          Select a parent user (Step 2).
                        </Alert>
                      ) : subordinateUsers.length === 0 ? (
                        <Alert severity="info" variant="outlined">
                          No subordinates found.
                        </Alert>
                      ) : (
                        <Autocomplete
                          multiple
                          disableCloseOnSelect
                          options={filteredSubs}
                          value={selectedSubordinates}
                          onChange={(_, value) => setSelectedSubordinates(value)}
                          getOptionLabel={(opt) => `${opt.username || "User"}${opt.email ? ` • ${opt.email}` : ""}`}
                          isOptionEqualToValue={(a, b) => a._id === b._id}
                          renderOption={(props, option, { selected }) => (
                            <li {...props} key={option._id}>
                              <Checkbox checked={selected} sx={{ mr: 1 }} />
                              <Stack sx={{ flex: 1 }}>
                                <Typography sx={{ fontWeight: 700 }}>{option.username || "User"}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {option.email || "-"} • {option.role || "-"}
                                </Typography>
                              </Stack>
                              <Chip size="small" label={option.role || "role"} variant="outlined" />
                            </li>
                          )}
                          renderInput={(params) => <TextField {...params} label="Selected subordinates" placeholder="Pick users..." />}
                        />
                      )}

                      {selectedSubordinates.length > 0 && (
                        <Alert severity="success" variant="outlined">
                          {selectedSubordinates.length} users selected.
                        </Alert>
                      )}
                    </Stack>
                  </Paper>
                </Stack>
              </RecipientTabPanel>
                </Paper>

                <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, borderColor: alpha(theme.palette.success.main, 0.14) }}>
                  <Stack direction="row" spacing={1.2} alignItems="center" sx={{ mb: 1.4 }}>
                    <Avatar sx={{ width: 34, height: 34, bgcolor: alpha(theme.palette.success.main, 0.12), color: theme.palette.success.main }}>2</Avatar>
                    <Box>
                      <Typography sx={{ fontWeight: 900, color: "#0f172a" }}>Step 2: Message Content</Typography>
                      <Typography variant="body2" color="text.secondary">Type your notification or SMS content.</Typography>
                    </Box>
                  </Stack>

                  <TextField
                    label="Message Content"
                    placeholder="Type message..."
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    multiline
                    minRows={5}
                    maxRows={12}
                    fullWidth
                    disabled={sending}
                    helperText={`${message.length}/1000`}
                  />

                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 1.2 }}>
                    <Typography variant="caption" color={message.trim() ? "success.main" : "text.secondary"}>
                      {message.trim() ? "Content looks good" : "Message cannot be empty"}
                    </Typography>
                    {message.trim() ? <CheckCircleOutlineRoundedIcon sx={{ color: theme.palette.success.main, fontSize: 20 }} /> : null}
                  </Stack>
                </Paper>

                <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, borderColor: alpha(theme.palette.secondary.main, 0.14) }}>
                  <Stack direction="row" spacing={1.2} alignItems="center" sx={{ mb: 1.4 }}>
                    <Avatar sx={{ width: 34, height: 34, bgcolor: alpha(theme.palette.secondary.main, 0.12), color: theme.palette.secondary.main }}>3</Avatar>
                    <Box>
                      <Typography sx={{ fontWeight: 900, color: "#0f172a" }}>Step 3: Preview & Send</Typography>
                      <Typography variant="body2" color="text.secondary">Confirm audience, channel and send instantly.</Typography>
                    </Box>
                  </Stack>

                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2.5, display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 1, bgcolor: alpha(theme.palette.primary.main, 0.02) }}>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
                        Ready to Send
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        To: <b>{recipientPayload.label || "-"}</b>
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Via: <b>{deliveryType}</b>
                      </Typography>
                    </Box>

                    <Button
                      variant="contained"
                      size="large"
                      startIcon={sending ? <CircularProgress size={18} /> : <SendIcon />}
                      onClick={handleSend}
                      disabled={!isFormValid || sending}
                      sx={{ borderRadius: 2.5, textTransform: "none", fontWeight: 900 }}
                    >
                      {sending ? "Sending..." : "Send Notification"}
                    </Button>
                  </Paper>

                  {deliveryType === "SMS" ? (
                    <Alert severity="warning" variant="outlined" sx={{ mt: 1.5, borderRadius: 2.5 }}>
                      SMS integration required. Credits may be used based on your provider configuration.
                    </Alert>
                  ) : (
                    <Alert severity="info" variant="outlined" sx={{ mt: 1.5, borderRadius: 2.5 }}>
                      Notification is instant for online users & saved in DB for inbox.
                    </Alert>
                  )}
                </Paper>
              </Stack>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} lg={5}>
          <Paper
            variant="outlined"
            sx={{
              borderRadius: 4,
              overflow: "hidden",
              borderColor: alpha(theme.palette.divider, 0.9),
              boxShadow: "0 18px 40px rgba(15,23,42,0.05)",
            }}
          >
            <Box sx={{ p: 2.2, display: "flex", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <HistoryOutlinedIcon sx={{ color: theme.palette.secondary.main }} />
                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                  Message History
                </Typography>
              </Stack>

              <Stack direction="row" spacing={1}>
                <Button size="small" startIcon={<SearchIcon />} variant="outlined" sx={{ borderRadius: 2.5 }}>
                  Filter
                </Button>
                <Button
                  size="small"
                  startIcon={<RefreshIcon />}
                  variant="outlined"
                  onClick={fetchHistory}
                  sx={{ borderRadius: 2.5 }}
                >
                  Refresh
                </Button>
              </Stack>
            </Box>

            <Divider />

            <Box sx={{ p: 2.2 }}>
              <TextField
                fullWidth
                value={historySearch}
                onChange={(e) => {
                  setHistorySearch(e.target.value);
                  setHistoryPage(1);
                }}
                placeholder="Search by message or recipient"
                InputProps={{
                  endAdornment: <SearchIcon sx={{ color: "text.secondary" }} />,
                }}
                sx={{ mb: 2 }}
              />

              {filteredHistory.length === 0 ? (
                <Alert severity="info" variant="outlined" sx={{ borderRadius: 2.5 }}>
                  No messages yet.
                </Alert>
              ) : isSmDown ? (
                <Stack spacing={1.2}>
                  {pagedHistory.map((h) => {
                    const isSms = (h.type || "").toLowerCase() === "sms";
                    const status = (h.status || "sent").toLowerCase();
                    return (
                      <Paper key={h._id} variant="outlined" sx={{ p: 1.5, borderRadius: 3 }}>
                        <Stack spacing={1}>
                          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Chip size="small" label={h.type || "Notification"} color={isSms ? "info" : "secondary"} />
                              <Chip size="small" label={h.status || "Sent"} color={status === "sent" || status === "delivered" ? "success" : status === "failed" ? "error" : "warning"} />
                            </Stack>
                            <Tooltip title="Preview full message">
                              <IconButton size="small" onClick={() => notify("info", h.message || "")}>
                                <MessageIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                          <Typography sx={{ fontWeight: 800, color: "#0f172a" }}>To: {h.recipient || "-"}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {String(h.message || "").length > 110 ? `${String(h.message).slice(0, 110)}…` : h.message}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {fmtDateTime(h.createdAt || h.date)}
                          </Typography>
                        </Stack>
                      </Paper>
                    );
                  })}
                </Stack>
              ) : (
                <ScrollableTableContainer sx={{ maxHeight: "calc(100vh - 360px)" }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 900, whiteSpace: "nowrap" }}>Type</TableCell>
                        <TableCell sx={{ fontWeight: 900, minWidth: 160 }}>To</TableCell>
                        <TableCell sx={{ fontWeight: 900, minWidth: 220 }}>Message</TableCell>
                        <TableCell sx={{ fontWeight: 900, whiteSpace: "nowrap" }}>Date & Time</TableCell>
                        <TableCell sx={{ fontWeight: 900, whiteSpace: "nowrap" }}>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pagedHistory.map((h) => {
                        const isSms = (h.type || "").toLowerCase() === "sms";
                        const status = (h.status || "sent").toLowerCase();
                        return (
                          <TableRow key={h._id} hover>
                            <TableCell>
                              <Chip size="small" label={h.type || "Notification"} color={isSms ? "info" : "secondary"} />
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700, color: "#0f172a" }}>
                              {h.recipient || "-"}
                            </TableCell>
                            <TableCell>
                              <Tooltip title={h.message || ""}>
                                <Typography variant="body2" sx={{ maxWidth: 280 }} noWrap>
                                  {h.message || "-"}
                                </Typography>
                              </Tooltip>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary">
                                {fmtDateTime(h.createdAt || h.date)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                label={h.status || "Sent"}
                                color={status === "sent" || status === "delivered" ? "success" : status === "failed" ? "error" : "warning"}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </ScrollableTableContainer>
              )}

              <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} spacing={1.2} sx={{ mt: 2 }}>
                <TextField
                  select
                  size="small"
                  value={historyRowsPerPage}
                  onChange={(e) => {
                    setHistoryRowsPerPage(Number(e.target.value));
                    setHistoryPage(1);
                  }}
                  sx={{ minWidth: 110 }}
                >
                  {[5, 10, 25].map((size) => (
                    <MenuItem key={size} value={size}>{size} / page</MenuItem>
                  ))}
                </TextField>
                <Pagination page={historyPage} count={historyPageCount} onChange={(_, value) => setHistoryPage(value)} shape="rounded" />
              </Stack>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Snackbar */}
      <Snackbar
        open={snack.open}
        autoHideDuration={4500}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          severity={snack.severity}
          variant="filled"
          sx={{ borderRadius: 2 }}
        >
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
